import axios from "axios";
import {checkToken} from "@/utils/lambdaUtils.js";
import {useAuthStore} from "@/stores/authStore.js";
import logger from "@/utils/logger.js";

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL + '/note/';

/**
 * Save a new note
 * POST /note
 */
export const createNote = async (newNote) => {
    if (!newNote) {
        throw new Error('Missing required note body object');
    }

    try {
        const store = useAuthStore();
        await checkToken();

        const response = await axios.post(`${baseUrl}`, newNote, {
            headers: {
                Authorization: `Bearer ${store.token}`,
            },
        });

        if (response.data === 'success') {
            return response.data;
        } else {
            logger.error('Error creating note', response.data);
            throw new Error(`Backend error creating note: ${response.data}`);
        }
    } catch (error) {
        logger.error('Error creating note', error);
        throw error;
    }
};

/**
 * Update an existing note
 * PUT /note/{noteId}
 */
export const updateNote = async (noteId, note) => {
    if (!noteId || !note) {
        throw new Error('Missing noteId or note object');
    }

    try {
        const store = useAuthStore();
        await checkToken();

        const response = await axios.put(`${baseUrl}${noteId}`, note, {
            headers: {
                Authorization: `Bearer ${store.token}`,
            },
        });

        if (response.data === 'success') {
            return response.data;
        } else {
            logger.error('Error updating note', response.data);
            throw new Error(`Backend error updating note: ${response.data}`);
        }
    } catch (error) {
        logger.error('Error updating note', error);
        throw error;
    }
};

/**
 * Delete a note
 * DELETE /note/{noteId}
 */
export const deleteNote = async (noteId) => {
    if (!noteId) {
        throw new Error('Missing noteId for deletion');
    }

    try {
        const store = useAuthStore();
        await checkToken();

        const response = await axios.delete(`${baseUrl}${noteId}`, {
            headers: {
                Authorization: `Bearer ${store.token}`,
            },
        });

        if (response.data === 'success') {
            return response.data;
        } else {
            logger.error('Error deleting note', response.data);
            throw new Error(`Backend error deleting note: ${response.data}`);
        }
    } catch (error) {
        logger.error('Error deleting note', error);
        throw error;
    }
};

/**
 * Get notes for a given parent (with optional version)
 * GET /note/by_parent/{parentType}/{parentId}/{parentVersion?}
 */
export const getNotesByParent = async (parentType, parentId, parentVersion = null) => {
    if (!parentType || !parentId) {
        throw new Error('Missing parentType or parentId');
    }

    try {
        const store = useAuthStore();
        await checkToken();

        let url = `${baseUrl}by_parent/${parentType}/${parentId}`;
        if (parentVersion !== null && parentVersion !== undefined) {
            url += `?parentVersion=${parentVersion}`;
        }

        const response = await axios.get(url, {
            headers: {
                Authorization: `Bearer ${store.token}`,
            },
        });

        return response.data;
    } catch (error) {
        logger.error('Error fetching notes by parent', error);
        throw error;
    }
};
